import React, { createContext, useContext, useState, useEffect } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
import { CartItem } from "../types/cart.types";
import { PastOrder } from "../types/order.types";
/**
* Represents the currently opened PastOrder card.
*/
interface OpenedCard { 
    products: CartItem[],
    order: PastOrder,
    time: string
    date:string,
    index: number
}
/**
* Shape of the PastOrderContext value.
*/
interface PastOrderContextType {
    /** The currently opened PastOrder card or undefined if none is open */
    openedPastOrderCard: OpenedCard | undefined;
    /**
    * Opens the PastOrder view for a given card.
    * @param card - The card to open PastOrder for
    */
    openPastOrder: (card: OpenedCard) => void;
    /** Closes the currently opened PastOrder view */
    closePastOrder: () => void;
    pastOrderOpen: boolean;
    setPastOrderOpen:(val: boolean)=>void
}

/**
* React context to manage PastOrder card state.
* Provides the currently opened PastOrder card and methods to open/close it.
*/
const PastOrderContext = createContext<PastOrderContextType | undefined>(undefined);
/**
* Provider component that wraps children and provides PastOrder card state.
* Stops background scroll when a PastOrder card is opened.
*/
export const PastOrderContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [openedPastOrderCard, setOpenedPastOrder] = useState<OpenedCard | undefined>(undefined) // PastOrder open flag
    
    const [pastOrderOpen, setPastOrderOpen] = useState<boolean>(false)
    // Open PastOrder
    const openPastOrder = (card: OpenedCard) => {
        setOpenedPastOrder(card);   
        setPastOrderOpen(true)
    } 

    // Close PastOrder Modal
    const closePastOrder = () => {
        setOpenedPastOrder(undefined);
        setPastOrderOpen(false)
    } 
    
    // Stop scroll on open
    useEffect(()=>{
            handleBackgroundScroll(openedPastOrderCard !== undefined)
    }, [openedPastOrderCard])

    return (
        <PastOrderContext.Provider value={{ openPastOrder, closePastOrder, openedPastOrderCard, pastOrderOpen, setPastOrderOpen }}>
        {children}
        </PastOrderContext.Provider>
    );
};
/**
* Hook to access the PastOrderContext value.
* Throws error if used outside of a PastOrderContextProvider.
*
* @returns The PastOrder card context with open/close methods and current card info.
*/
export const usePastOrderContext = () => {
    const context = useContext(PastOrderContext);
    if (!context) throw new Error("usePastOrderContext must be used within a PastOrderContextProvider");
    return context;
};